import { ThemeUICSSObject } from '@apia/theme';
import { SetStateAction, useCallback, useRef } from 'react';

type InitialStyleAssigner = (el: HTMLElement) => Partial<ThemeUICSSObject>;
type CSSState = Partial<ThemeUICSSObject>;

type UseStyleStateProps = {
  initialState?: CSSState | InitialStyleAssigner;
  onUpdateRef?: InitialStyleAssigner;
};

export function applyStylesToElement(el: HTMLElement, styles: CSSState) {
  Object.entries(styles).forEach(([name, value]) => {
    try {
      el.style[name as 'width'] = value as any;
    } catch (e) {
      e; // Se intentó asignar un valor que no es correcto
    }
  });
}

/**
 * Este hook permite mantener un estado de estilos, para utilizar en un elemento del DOM.
 *
 * A diferencia de un setState común, al realizar setState en este hook no se re-renderiza el componente, sino que se aplican los estilos directamente en el DOM.
 *
 * Se garantiza además que estos estados no se perderán en los re-renderizados provocados por causas externas a este hook.
 */
export function useStyleState(props?: UseStyleStateProps) {
  const element = useRef<null | HTMLElement>(null);
  const state = useRef<CSSState>(
    typeof props?.initialState === 'object'
      ? props?.initialState
      : ({} as CSSState),
  );

  const applyStyles = useCallback(() => {
    applyStylesToElement(element.current!, state.current);
  }, []);

  const hasInitializedState = useRef(typeof props?.initialState !== 'function');
  return {
    ref: useCallback(
      (el: HTMLElement | null) => {
        element.current = el;
        if (el) {
          if (!hasInitializedState.current) {
            hasInitializedState.current = true;
            state.current = (props?.initialState as InitialStyleAssigner)(el);
          }

          if (props?.onUpdateRef) {
            state.current = props.onUpdateRef(el);
          }

          applyStyles();
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [applyStyles],
    ),
    setState: useCallback(
      (newState: SetStateAction<CSSState>) => {
        state.current =
          typeof newState === 'function' ? newState(state.current) : newState;
        applyStyles();
      },
      [applyStyles],
    ),
  };
}
